import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import publicAdmissionsService from '../../services/publicAdmissionsService';

function DepositPaymentPage() {
  const { schoolCode, depositId } = useParams();
  const [deposit, setDeposit] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const [payment, setPayment] = useState(null); // { url, reference, amount }
  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle | waiting | success | failed
  const paymentWindowRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const loadDeposit = useCallback(() => {
    setLoading(true);
    publicAdmissionsService.getDepositInfo(schoolCode, depositId)
      .then((data) => setDeposit(data))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [schoolCode, depositId]);

  useEffect(() => { loadDeposit(); }, [loadDeposit]);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
        paymentWindowRef.current.close();
      }
    };
  }, []);

  const checkStatus = useCallback(async (reference) => {
    try {
      const result = await publicAdmissionsService.checkDepositPaymentStatus(schoolCode, depositId, reference);
      if (result.status === 'success') {
        setPaymentStatus('success');
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        loadDeposit();
      } else if (result.status === 'failed') {
        setPaymentStatus('failed');
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      }
    } catch (error) {
      // transient — keep polling
    }
  }, [schoolCode, depositId, loadDeposit]);

  const startPaymentPolling = (reference) => {
    setPaymentStatus('waiting');
    checkStatus(reference);
    pollIntervalRef.current = setInterval(() => checkStatus(reference), 4000);
  };

  const openPaymentWindow = (url, reference) => {
    const win = window.open(url, 'deposit_payment', 'width=800,height=700');
    if (!win) {
      toast.error('Please enable pop-ups to complete the deposit payment, then use "Check payment status" below.');
      setPaymentStatus('waiting');
      checkStatus(reference);
      return;
    }
    paymentWindowRef.current = win;
    startPaymentPolling(reference);

    const closeWatcher = setInterval(() => {
      if (win.closed) {
        clearInterval(closeWatcher);
        checkStatus(reference);
      }
    }, 500);
  };

  const handlePay = async () => {
    setStarting(true);
    try {
      const result = await publicAdmissionsService.payDeposit(schoolCode, depositId);
      setPayment({ url: result.payment_url, reference: result.reference, amount: result.amount });
      openPaymentWindow(result.payment_url, result.reference);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to start payment. Please try again.');
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-page">
        <div className="auth-container"><p>Loading...</p></div>
      </div>
    );
  }

  if (loadError || !deposit) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <h1>Deposit Not Found</h1>
            <p>We couldn't find this deposit. Please check the link or contact the school directly.</p>
          </div>
          <div className="auth-footer"><Link to="/">Back to home</Link></div>
        </div>
      </div>
    );
  }

  if (deposit.status === 'paid' || paymentStatus === 'success') {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <h1>Deposit Settled</h1>
            <p>The admission deposit for {deposit.applicant_name} has been paid in full. Thank you!</p>
          </div>
          <div className="auth-footer"><Link to="/">Back to home</Link></div>
        </div>
      </div>
    );
  }

  if (deposit.status === 'waived') {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <h1>Deposit Waived</h1>
            <p>The school has waived this admission deposit — no payment is required.</p>
          </div>
          <div className="auth-footer"><Link to="/">Back to home</Link></div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1>Admission Deposit</h1>
          <p>Securing {deposit.applicant_name}'s admission</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Required</span><strong>GHS {Number(deposit.required_amount).toFixed(2)}</strong></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Paid so far</span><strong>GHS {Number(deposit.paid_amount).toFixed(2)}</strong></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Balance due</span><strong>GHS {Number(deposit.balance).toFixed(2)}</strong></div>
          {deposit.due_date && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Due date</span><strong>{deposit.due_date}</strong></div>}
        </div>

        {paymentStatus === 'waiting' && payment && (
          <p>A payment window has opened for GHS {Number(payment.amount).toFixed(2)}. Complete the payment there — this page will update automatically once it's confirmed.</p>
        )}
        {paymentStatus === 'failed' && <p>The payment didn't go through. You can try again below.</p>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button type="button" className="btn btn-primary btn-full" onClick={payment ? () => openPaymentWindow(payment.url, payment.reference) : handlePay} disabled={starting}>
            {starting ? 'Starting...' : paymentStatus === 'failed' ? 'Try Payment Again' : `Pay GHS ${Number(deposit.balance).toFixed(2)}`}
          </button>
          {payment && (
            <button type="button" className="btn btn-secondary btn-full" onClick={() => checkStatus(payment.reference)}>
              Check Payment Status
            </button>
          )}
        </div>

        <div className="auth-footer"><Link to="/">Back to home</Link></div>
      </div>
    </div>
  );
}

export default DepositPaymentPage;
